import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import api from '../api/client';

// Module-level cache so hovering the same team twice doesn't refetch.
const memberCache = new Map(); // teamId -> Promise<members[]>

function fetchMembers(teamId) {
  if (!memberCache.has(teamId)) {
    memberCache.set(
      teamId,
      api.get(`/api/teams/${teamId}`).then((r) => r.data.members || []).catch(() => [])
    );
  }
  return memberCache.get(teamId);
}

/**
 * Wraps a team name so hovering it shows a popover with the team's players,
 * each linking to their public profile. Pass `team` (with an optional
 * `members` array already attached) when you have it, or `teamId` + `name`
 * to have the player list lazy-loaded on first hover.
 */
export default function TeamHoverName({ team, teamId, name, className, children }) {
  const id = team?.id ?? teamId;
  const label = children ?? team?.name ?? name;
  const [members, setMembers] = useState(team?.members || null);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);
  const hideTimer = useRef(null);

  if (!id || !label) return <span className={className}>{label}</span>;

  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; } };
  const scheduleHide = () => { cancelHide(); hideTimer.current = setTimeout(() => setPos(null), 150); };

  const handleEnter = () => {
    cancelHide();
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
    if (members === null) {
      setLoading(true);
      fetchMembers(id).then((m) => { setMembers(m); setLoading(false); });
    }
  };

  const confirmed = (members || []).filter((m) => m.status === 'confirmed');

  return (
    <span
      ref={anchorRef}
      className={`${className || ''} cursor-default`.trim()}
      onMouseEnter={handleEnter}
      onMouseLeave={scheduleHide}
      onFocus={handleEnter}
      onBlur={scheduleHide}
    >
      {label}
      {pos && createPortal(
        <div
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%)' }}
          className="z-[100] w-52 max-w-[80vw] bg-slate-800 text-white rounded-lg shadow-xl p-2.5 text-xs"
        >
          <div className="font-bold text-slate-300 uppercase tracking-wide text-[10px] mb-1.5">Spieler</div>
          {loading && members === null ? (
            <div className="text-slate-400">Lädt…</div>
          ) : confirmed.length === 0 ? (
            <div className="text-slate-400">Keine Spieler</div>
          ) : (
            <ul className="space-y-1">
              {confirmed.map((m) => (
                <li key={m.id}>
                  <Link
                    to={`/users/${m.user_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-white hover:text-ocean-300 hover:underline"
                  >
                    {m.user?.nickname || m.user?.username || '–'}
                    {m.role === 'creator' && <span className="ml-1 text-[9px] text-sand-300">★</span>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>,
        document.body
      )}
    </span>
  );
}
